import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { TrendingUp, RefreshCw, Target, Users, Award, ArrowRight, CreditCard, MousePointerClick } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { DashboardLayout } from "../components/layout/DashboardLayout";

const COLORS = {
  principale: '#210875',
  accent: '#897FCB',
  success: '#059669',
  warning: '#f59e0b',
  error: '#dc2626',
};

const PLAN_LABELS = { monthly: "Mensuel", annual: "Annuel", earlybird: "🎁 Early Bird" };

export const AdminMarketingPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const api = useApi();

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/marketing/stats");
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur chargement stats");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout>
        <div className="text-center py-20 text-muted-foreground">
          Aucune donnée à afficher pour l'instant.
        </div>
      </DashboardLayout>
    );
  }

  const { funnel, by_utm, by_plan, latest_payments, top_referrers, total_revenue } = data;

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-7xl">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: COLORS.principale }}>📊 Marketing & Conversion</h1>
            <p className="text-muted-foreground mt-1">Funnel, sources de trafic et revenus</p>
          </div>
          <button
            onClick={fetchStats}
            data-testid="refresh-stats-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-lg border hover:bg-muted transition-colors"
          >
            <RefreshCw size={16} /> Rafraîchir
          </button>
        </div>

        {/* KPIs principaux */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPI icon={<MousePointerClick size={20} />} label="Checkouts créés" value={funnel.checkouts_created} />
          <KPI icon={<CreditCard size={20} />} label="Paiements confirmés" value={funnel.checkouts_paid} accent={COLORS.success} />
          <KPI icon={<Target size={20} />} label="Comptes activés" value={funnel.activations_consumed} accent={COLORS.accent} />
          <KPI icon={<TrendingUp size={20} />} label="Revenu total" value={`${total_revenue.toFixed(2)}€`} accent={COLORS.principale} />
        </div>

        {/* Funnel visuel */}
        <div className="bg-white rounded-xl border p-6">
          <h2 className="font-bold mb-4 flex items-center gap-2"><Target size={18} /> Funnel de conversion</h2>
          <FunnelStep label="Checkout Stripe créé" value={funnel.checkouts_created} />
          <FunnelArrow rate={`${funnel.checkout_to_paid_rate}%`} />
          <FunnelStep label="Paiement validé" value={funnel.checkouts_paid} color={COLORS.success} />
          <FunnelArrow rate={`${funnel.paid_to_activated_rate}%`} />
          <FunnelStep label="Compte activé (password choisi)" value={funnel.activations_consumed} color={COLORS.accent} />
          {funnel.checkouts_paid > funnel.activations_consumed && (
            <p className="mt-3 text-xs text-amber-600">
              ⚠️ {funnel.checkouts_paid - funnel.activations_consumed} utilisateur(s) ont payé mais pas activé leur compte. Les relances automatiques J+1 et J+3 sont en cours.
            </p>
          )}
        </div>

        {/* Répartition par plan */}
        <div className="bg-white rounded-xl border p-6">
          <h2 className="font-bold mb-4 flex items-center gap-2"><Award size={18} /> Répartition par formule</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {by_plan.map((p) => (
              <div key={p.plan} className="p-4 rounded-lg bg-muted/40">
                <div className="text-xs font-medium text-muted-foreground">{PLAN_LABELS[p.plan] || p.plan}</div>
                <div className="text-2xl font-bold mt-1" style={{ color: COLORS.principale }}>{p.count}</div>
                <div className="text-sm" style={{ color: COLORS.success }}>{p.revenue.toFixed(2)}€</div>
              </div>
            ))}
          </div>
        </div>

        {/* Par UTM source */}
        <div className="bg-white rounded-xl border p-6">
          <h2 className="font-bold mb-1 flex items-center gap-2"><TrendingUp size={18} /> Par source (UTM)</h2>
          <p className="text-xs text-muted-foreground mb-4">
            Ajoute <code className="bg-muted px-1 py-0.5 rounded text-xs">?utm_source=linkedin&utm_campaign=lancement</code> à tes liens pour tracker.
          </p>
          {by_utm.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              Aucune donnée UTM pour l'instant. Les visiteurs sans UTM apparaîtront en "direct".
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-xs uppercase text-muted-foreground">
                    <th className="text-left py-2">Source</th>
                    <th className="text-left py-2">Medium</th>
                    <th className="text-left py-2">Campagne</th>
                    <th className="text-right py-2">Checkouts</th>
                    <th className="text-right py-2">Payés</th>
                    <th className="text-right py-2">Taux</th>
                    <th className="text-right py-2">Revenu</th>
                  </tr>
                </thead>
                <tbody>
                  {by_utm.map((row, i) => (
                    <tr key={i} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="py-3 font-medium">{row.source}</td>
                      <td className="py-3 text-muted-foreground">{row.medium}</td>
                      <td className="py-3 text-muted-foreground">{row.campaign}</td>
                      <td className="text-right py-3">{row.clicks_checkout}</td>
                      <td className="text-right py-3" style={{ color: COLORS.success }}>{row.paid}</td>
                      <td className="text-right py-3">{row.conversion_rate}%</td>
                      <td className="text-right py-3 font-bold" style={{ color: COLORS.principale }}>
                        {row.revenue.toFixed(2)}€
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Top parrains */}
        <div className="bg-white rounded-xl border p-6">
          <h2 className="font-bold mb-4 flex items-center gap-2"><Users size={18} /> Top parrains</h2>
          {top_referrers.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Aucun parrainage converti pour l'instant.</p>
          ) : (
            <div className="space-y-2">
              {top_referrers.map((ref, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/40">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: COLORS.accent }}>
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{ref.prenom} {ref.nom}</div>
                    <div className="text-xs text-muted-foreground">{ref.email} · code <code className="bg-white px-1 rounded">{ref.referral_code}</code></div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold" style={{ color: COLORS.success }}>{ref.conversions}</div>
                    <div className="text-xs text-muted-foreground">conversions</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Derniers paiements */}
        <div className="bg-white rounded-xl border p-6">
          <h2 className="font-bold mb-4 flex items-center gap-2"><CreditCard size={18} /> Derniers paiements</h2>
          {latest_payments.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Aucun paiement pour l'instant.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-xs uppercase text-muted-foreground">
                    <th className="text-left py-2">Date</th>
                    <th className="text-left py-2">Email</th>
                    <th className="text-left py-2">Formule</th>
                    <th className="text-right py-2">Montant</th>
                    <th className="text-left py-2">Source</th>
                    <th className="text-left py-2">Parrain</th>
                  </tr>
                </thead>
                <tbody>
                  {latest_payments.map((p, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-3 text-muted-foreground text-xs">{new Date(p.created_at).toLocaleString('fr-FR')}</td>
                      <td className="py-3">{p.email}</td>
                      <td className="py-3">{PLAN_LABELS[p.plan] || p.plan}</td>
                      <td className="text-right py-3 font-bold" style={{ color: COLORS.success }}>{p.amount?.toFixed(2)}€</td>
                      <td className="py-3">{p.utm_source}</td>
                      <td className="py-3 text-xs">{p.referral_code || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Guide UTM rapide */}
        <div className="bg-gradient-to-br from-purple-50 to-orange-50 rounded-xl border-2 border-purple-200 p-6">
          <h2 className="font-bold mb-2">💡 Comment tracker tes campagnes</h2>
          <p className="text-sm text-muted-foreground mb-3">
            Ajoute les paramètres UTM à la fin de tes liens pour voir d'où viennent tes conversions :
          </p>
          <ul className="text-sm space-y-1 font-mono bg-white rounded p-3 border">
            <li><strong>LinkedIn :</strong> <code>...?utm_source=linkedin&utm_medium=post&utm_campaign=lancement</code></li>
            <li><strong>Instagram :</strong> <code>...?utm_source=instagram&utm_medium=story</code></li>
            <li><strong>Email :</strong> <code>...?utm_source=email&utm_campaign=launch_w1</code></li>
            <li><strong>Affilié :</strong> <code>...?utm_source=partner&utm_content=tiphaine</code></li>
          </ul>
        </div>

      </div>
    </DashboardLayout>
  );
};

const KPI = ({ icon, label, value, accent = COLORS.principale }) => (
  <div className="bg-white rounded-xl border p-4">
    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
      <div style={{ color: accent }}>{icon}</div>
      <span>{label}</span>
    </div>
    <div className="text-2xl font-bold" style={{ color: accent }}>{value}</div>
  </div>
);

const FunnelStep = ({ label, value, color = COLORS.principale }) => (
  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
    <span className="font-medium">{label}</span>
    <span className="text-xl font-bold" style={{ color }}>{value}</span>
  </div>
);

const FunnelArrow = ({ rate }) => (
  <div className="flex items-center justify-center py-2 text-sm text-muted-foreground">
    <ArrowRight size={16} className="rotate-90" />
    <span className="ml-2 font-medium">{rate} de conversion</span>
  </div>
);
